import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Map, { Marker, Source, Layer, NavigationControl } from "react-map-gl";
import { Navigation, X, MapPin, Clock, Route, Maximize2, Minimize2, Car, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const fetchRoute = async (start, end) => {
  const res = await fetch(
    `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lng},${start.lat};${end.lng},${end.lat}?geometries=geojson&overview=full&steps=true&access_token=${MAPBOX_TOKEN}`
  );
  const data = await res.json();
  if (data.code === "Ok" && data.routes?.[0]) {
    return {
      geometry: data.routes[0].geometry,
      distance: data.routes[0].distance,
      duration: data.routes[0].duration,
      steps: data.routes[0].legs?.[0]?.steps || [],
    };
  }
  return null;
};

const routeLayer = {
  id: "directions-route",
  type: "line",
  paint: {
    "line-color": "#059669",
    "line-width": 5,
    "line-opacity": 0.85,
  },
};

const routeOutlineLayer = {
  id: "directions-route-outline",
  type: "line",
  paint: {
    "line-color": "#022c22",
    "line-width": 8,
    "line-opacity": 0.3,
  },
};

const DirectionsMapOverlay = ({ proLocation, customerLocation, customerAddress, customerName, onClose, fullScreen = false }) => {
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(fullScreen);
  const [viewState, setViewState] = useState({
    latitude: customerLocation?.lat || 43.65,
    longitude: customerLocation?.lng || -79.38,
    zoom: 13,
  });

  const loadRoute = useCallback(async () => {
    if (!proLocation || !customerLocation) { setLoading(false); return; }
    setLoading(true);
    const route = await fetchRoute(
      { lat: proLocation.lat, lng: proLocation.lng },
      { lat: customerLocation.lat, lng: customerLocation.lng }
    );
    setRouteData(route);
    if (route) {
      const coords = route.geometry.coordinates;
      const lats = coords.map(c => c[1]);
      const lngs = coords.map(c => c[0]);
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const latSpan = Math.max(...lats) - Math.min(...lats);
      const lngSpan = Math.max(...lngs) - Math.min(...lngs);
      const maxSpan = Math.max(latSpan, lngSpan);
      const zoom = maxSpan > 0.5 ? 9 : maxSpan > 0.2 ? 10 : maxSpan > 0.05 ? 12 : 13;
      setViewState({ latitude: centerLat, longitude: centerLng, zoom });
    }
    setLoading(false);
  }, [proLocation, customerLocation]);

  useEffect(() => { loadRoute(); }, [loadRoute]);

  const openExternalNav = () => {
    if (!customerLocation) return;
    const dest = `${customerLocation.lat},${customerLocation.lng}`;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const url = isIOS
      ? `maps://maps.apple.com/?daddr=${dest}`
      : `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
    window.open(url, "_blank");
  };

  const distanceKm = routeData ? (routeData.distance / 1000).toFixed(1) : null;
  const etaMin = routeData ? Math.ceil(routeData.duration / 60) : null;

  const routeGeoJSON = routeData ? { type: "Feature", geometry: routeData.geometry } : null;

  const mapContent = (
    <div className="relative w-full h-full" data-testid="directions-map">
      {loading && (
        <div className="absolute inset-0 z-10 bg-white/80 flex items-center justify-center">
          <div className="flex items-center gap-2 text-emerald-700">
            <Route className="w-5 h-5 animate-spin" /> Calculating route...
          </div>
        </div>
      )}
      <Map
        {...viewState}
        onMove={(e) => setViewState(e.viewState)}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="top-right" showCompass={false} />

        {routeGeoJSON && (
          <Source id="directions-source" type="geojson" data={routeGeoJSON}>
            <Layer {...routeOutlineLayer} />
            <Layer {...routeLayer} />
          </Source>
        )}

        {proLocation && (
          <Marker longitude={proLocation.lng} latitude={proLocation.lat} anchor="center">
            <div className="relative">
              <div className="absolute w-10 h-10 bg-emerald-400/25 rounded-full animate-ping" style={{ top: "-8px", left: "-8px" }} />
              <div className="w-7 h-7 bg-emerald-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                <Car className="w-3.5 h-3.5 text-white" />
              </div>
            </div>
          </Marker>
        )}

        {customerLocation && (
          <Marker longitude={customerLocation.lng} latitude={customerLocation.lat} anchor="bottom">
            <div className="flex flex-col items-center">
              <div className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full mb-1 whitespace-nowrap shadow-md">
                Customer
              </div>
              <MapPin className="w-8 h-8 text-red-600 drop-shadow-lg" fill="#dc2626" />
            </div>
          </Marker>
        )}
      </Map>

      {/* Info bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-stone-200 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            {etaMin && (
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span className="text-lg font-bold text-emerald-950">{etaMin} min</span>
              </div>
            )}
            {distanceKm && (
              <div className="flex items-center gap-1.5">
                <Route className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-stone-600">{distanceKm} km</span>
              </div>
            )}
            <div className="hidden sm:block text-sm text-stone-500 truncate min-w-0">
              <MapPin className="w-3.5 h-3.5 inline mr-1 text-red-500" />
              {customerAddress || "Customer location"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => setIsFullScreen(!isFullScreen)}
              data-testid="toggle-fullscreen"
            >
              {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1.5"
              onClick={openExternalNav}
              data-testid="navigate-btn"
            >
              <Navigation className="w-4 h-4" /> Navigate
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isFullScreen) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-white"
          data-testid="directions-fullscreen"
        >
          <div className="absolute top-4 left-4 z-20 flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              className="bg-white/90 backdrop-blur rounded-full shadow-lg"
              onClick={() => { setIsFullScreen(false); onClose?.(); }}
              data-testid="close-fullscreen"
            >
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
            <div className="bg-white/90 backdrop-blur rounded-full px-3 py-1.5 shadow-lg">
              <p className="text-sm font-medium text-emerald-950">
                <Navigation className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />
                Directions to {customerName || "Customer"}
              </p>
            </div>
          </div>
          <div className="w-full h-full">{mapContent}</div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return mapContent;
};

export default DirectionsMapOverlay;
